import { apiClient } from '@/lib/api-client';
import type { OfferOption } from '@/shared/offer-search/offers-api.datasource';
import type {
  TransactionsListResponse,
  TransactionDetail,
  TransactionInvoices,
  InvoiceTransaction,
  TransactionContract,
  BillingAgreement,
  TransactionOfferAndArrangements,
  Offer,
  TransactionEventsResponse,
  SalesCorrelatedEntityEventsResponse,
  SalesAuditEntityType,
  CancelBillingAgreementInput,
  CancelBillingAgreementResult,
  ResolveCycleCountInput,
  ResolveCycleCountResult,
  SyncChargeHistoryInput,
  SyncChargeHistoryResult,
  SyncContractChargeHistoryResult,
  CreateDebtArrangementInput,
  ActivateDebtArrangementInput,
  UpdateDebtArrangementDraftInput,
  DebtArrangement,
  DebtArrangementWithRelations,
  DebtArrangementReasonInput,
  AbandonContractInput,
  AbandonContractResult,
  ContractsListResponse,
  ContractDetail,
  ContractInvoices,
  ContractBillingAgreements,
  BillingAgreementDetail,
  ContractStatus,
  Provider,
  ProviderAccount,
  Tenant,
  TransactionOrigin,
  TransactionStatus,
  SaleWebhookInboxStatus,
  WebhookEventType,
  SaleWebhookInboxListResponse,
  SaleWebhookInbox,
  ForceProcessSaleWebhookInboxInput,
  JustifiedActionInput,
  ManuallySettleInvoiceInput,
  ManuallySettleInvoiceResult,
  InvalidateManualTransactionResult,
  UploadAttachmentResult,
  CloseOperationalAlertInput,
  ListSalesOperationalAlertsInput,
  OperationalAlertFacet,
  SalesOperationalAlertStatus,
  SalesOperationalAlert,
  SalesOperationalAlertsListResponse,
} from '../../domain/entities/sales.entity';

/**
 * Filtros server-side da listagem de Vendas — espelham o
 * `ListTransactionsQueryDto` do monolith. `externalId` casa por substring;
 * as datas são dia (`YYYY-MM-DD`), com as bordas inclusive.
 *
 * `name`/`email` do contato ficam fora de propósito: vêm do Identity depois da
 * página resolvida, então seguem filtrados no client (ver
 * `presentation/shared/sales-filters.ts`).
 */
export interface ListTransactionsInput {
  page?: number;
  perPage?: number;
  /** Contato escolhido no filtro (Transaction.userId). */
  userId?: string;
  externalId?: string;
  status?: TransactionStatus;
  businessUnit?: Tenant;
  /** Id interno da oferta — aba "transactions" de `/offers/:id`. */
  offerId?: string;
  /** Id externo (gateway) da oferta — match exato, não substring. */
  offerExternalId?: string;
  /** Id externo (gateway) do produto — match exato, não substring. */
  productExternalId?: string;
  origin?: TransactionOrigin;
  /** "Cobrada entre" — faixa de `occurredAt`, o instante no gateway. */
  occurredFrom?: string;
  occurredTo?: string;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  /** Ausente = default do back (`occurredAt` desc). */
  sortBy?: 'occurredAt' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
}

/**
 * Filtros server-side da listagem de Contratos — espelham o
 * `ListContractsQueryDto`. `tenant` e `product` são resolvidos no back pela BA
 * atual do contrato. `name` do contato segue no client, como em Vendas.
 */
export interface ListContractsInput {
  page?: number;
  perPage?: number;
  /** Contato escolhido no filtro (userId do contrato). */
  userId?: string;
  contractId?: string;
  product?: string;
  status?: ContractStatus;
  tenant?: Tenant;
  startedFrom?: string;
  startedTo?: string;
  endedFrom?: string;
  endedTo?: string;
}

/** Filtros da tela de Ofertas — espelham o `FindOffersQueryDto`. */
export interface ListOffersInput {
  page?: number;
  perPage?: number;
  /** Substring no nome da oferta. */
  name?: string;
  /** Id exato da oferta ou do produto no gateway. */
  externalId?: string;
  providerAccountId?: string;
}

export interface OffersListResponse {
  items: OfferOption[];
  total: number;
  page: number;
  perPage: number;
}

export interface ListProviderAccountsInput {
  provider?: Provider;
}

/**
 * Filtros server-side do log de webhook inbox — espelham o
 * `ListSaleWebhookInboxQueryDto` do monolith. `from`/`to` são ISO date.
 */
export interface ListSaleWebhookInboxInput {
  page?: number;
  perPage?: number;
  status?: SaleWebhookInboxStatus;
  eventType?: WebhookEventType;
  providerAccountId?: string;
  idempotenceSource?: string;
  from?: string;
  to?: string;
}

/**
 * Datasource do Sales Admin — 1 método por endpoint de leitura. Cada endpoint do
 * detalhe resolve a cadeia `Transaction → Invoice → BA → Contract/Offer` no
 * back, para o front carregar cada aba sob demanda.
 */
export class SalesApiDatasource {
  async listTransactions(
    input?: ListTransactionsInput,
  ): Promise<TransactionsListResponse> {
    const { data } = await apiClient.get<TransactionsListResponse>(
      '/admin/sales/transactions',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          userId: input?.userId || undefined,
          externalId: input?.externalId || undefined,
          status: input?.status || undefined,
          businessUnit: input?.businessUnit || undefined,
          offerId: input?.offerId || undefined,
          offerExternalId: input?.offerExternalId || undefined,
          productExternalId: input?.productExternalId || undefined,
          origin: input?.origin || undefined,
          occurredFrom: input?.occurredFrom || undefined,
          occurredTo: input?.occurredTo || undefined,
          createdFrom: input?.createdFrom || undefined,
          createdTo: input?.createdTo || undefined,
          updatedFrom: input?.updatedFrom || undefined,
          updatedTo: input?.updatedTo || undefined,
          sortBy: input?.sortBy,
          sortDirection: input?.sortDirection,
        },
      },
    );
    return data;
  }

  async getTransaction(id: string): Promise<TransactionDetail> {
    const { data } = await apiClient.get<TransactionDetail>(
      `/admin/sales/transactions/${id}`,
    );
    return data;
  }

  async getTransactionInvoices(id: string): Promise<TransactionInvoices> {
    const { data } = await apiClient.get<TransactionInvoices>(
      `/admin/sales/transactions/${id}/invoices`,
    );
    return data;
  }

  async getTransactionContract(id: string): Promise<TransactionContract> {
    const { data } = await apiClient.get<TransactionContract>(
      `/admin/sales/transactions/${id}/contract`,
    );
    return data;
  }

  async getTransactionBillingAgreement(id: string): Promise<BillingAgreement> {
    const { data } = await apiClient.get<BillingAgreement>(
      `/admin/sales/transactions/${id}/billing-agreement`,
    );
    return data;
  }

  async getTransactionOfferAndArrangements(
    id: string,
  ): Promise<TransactionOfferAndArrangements> {
    const { data } = await apiClient.get<TransactionOfferAndArrangements>(
      `/admin/sales/transactions/${id}/offer-and-arrangements`,
    );
    return data;
  }

  async getTransactionEvents(
    id: string,
    params: { page: number; perPage: number },
  ): Promise<TransactionEventsResponse> {
    const { data } = await apiClient.get<TransactionEventsResponse>(
      `/admin/sales/transactions/${id}/events`,
      { params },
    );
    return data;
  }

  async getEntityEvents(
    entityType: SalesAuditEntityType,
    entityId: string,
    params: { page: number; perPage: number },
  ): Promise<TransactionEventsResponse> {
    const { data } = await apiClient.get<TransactionEventsResponse>(
      `/admin/sales/entities/${entityType}/${entityId}/events`,
      { params },
    );
    return data;
  }

  async getEntityEventsByCorrelationId(
    correlationId: string,
    params: { page: number; perPage: number },
  ): Promise<SalesCorrelatedEntityEventsResponse> {
    const { data } = await apiClient.get<SalesCorrelatedEntityEventsResponse>(
      '/admin/sales/entity-events',
      { params: { correlationId, ...params } },
    );
    return data;
  }

  // --- Contratos ---

  async listContracts(
    input?: ListContractsInput,
  ): Promise<ContractsListResponse> {
    const { data } = await apiClient.get<ContractsListResponse>(
      '/admin/sales/contracts',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          userId: input?.userId || undefined,
          contractId: input?.contractId || undefined,
          product: input?.product || undefined,
          status: input?.status || undefined,
          tenant: input?.tenant || undefined,
          startedFrom: input?.startedFrom || undefined,
          startedTo: input?.startedTo || undefined,
          endedFrom: input?.endedFrom || undefined,
          endedTo: input?.endedTo || undefined,
        },
      },
    );
    return data;
  }

  async getContract(id: string): Promise<ContractDetail> {
    const { data } = await apiClient.get<ContractDetail>(
      `/admin/sales/contracts/${id}`,
    );
    return data;
  }

  async getContractInvoices(id: string): Promise<ContractInvoices> {
    const { data } = await apiClient.get<ContractInvoices>(
      `/admin/sales/contracts/${id}/invoices`,
    );
    return data;
  }

  async getContractOfferAndArrangements(
    id: string,
  ): Promise<TransactionOfferAndArrangements> {
    const { data } = await apiClient.get<TransactionOfferAndArrangements>(
      `/admin/sales/contracts/${id}/offer-and-arrangements`,
    );
    return data;
  }

  async getContractBillingAgreements(
    id: string,
  ): Promise<ContractBillingAgreements> {
    const { data } = await apiClient.get<ContractBillingAgreements>(
      `/admin/sales/contracts/${id}/billing-agreements`,
    );
    return data;
  }

  async getBillingAgreement(id: string): Promise<BillingAgreementDetail> {
    const { data } = await apiClient.get<BillingAgreementDetail>(
      `/admin/sales/billing-agreements/${id}`,
    );
    return data;
  }

  /** Página `/debt-arrangements/:id`: a DA com as BAs substituída e resultante. */
  async getDebtArrangement(id: string): Promise<DebtArrangementWithRelations> {
    const { data } = await apiClient.get<DebtArrangementWithRelations>(
      `/admin/sales/debt-arrangements/${id}`,
    );
    return data;
  }

  /** Tela `/offers`: lista paginada, ordenada por nome. */
  async listOffers(input?: ListOffersInput): Promise<OffersListResponse> {
    const { data } = await apiClient.get<OffersListResponse>(
      '/admin/sales/offers',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          name: input?.name || undefined,
          externalId: input?.externalId || undefined,
          providerAccountId: input?.providerAccountId || undefined,
        },
      },
    );
    return data;
  }

  /** Página `/offers/:id`: a oferta com o gateway e a conta já resolvidos. */
  async getOffer(id: string): Promise<Offer> {
    const { data } = await apiClient.get<Offer>(`/admin/sales/offers/${id}`);
    return data;
  }

  /**
   * Tentativas de cobrança de uma fatura — caminho inverso do
   * `transactions/:id/invoices`, consumido pelo modal "Resumo da Fatura".
   */
  async getInvoiceTransactions(id: string): Promise<InvoiceTransaction[]> {
    const { data } = await apiClient.get<InvoiceTransaction[]>(
      `/admin/sales/invoices/${id}/transactions`,
    );
    return data;
  }

  // --- Ações (mutations) ---

  async cancelBillingAgreement(
    billingAgreementId: string,
    body: CancelBillingAgreementInput,
  ): Promise<CancelBillingAgreementResult> {
    const { data } = await apiClient.post<CancelBillingAgreementResult>(
      `/admin/sales/billing-agreements/${billingAgreementId}/cancel`,
      body,
    );
    return data;
  }

  /**
   * Dispara à mão a resolução do total de ciclos pela API do gateway (T2) — o
   * mesmo caminho que a ingestão de webhook tenta automaticamente. Ao contrário
   * dela, aqui a falha do gateway volta como erro HTTP para o operador ver.
   */
  async resolveBillingAgreementCycleCount(
    billingAgreementId: string,
    body: ResolveCycleCountInput,
  ): Promise<ResolveCycleCountResult> {
    const { data } = await apiClient.post<ResolveCycleCountResult>(
      `/admin/sales/billing-agreements/${billingAgreementId}/resolve-cycle-count`,
      body,
    );
    return data;
  }

  /**
   * Reconstrói as cobranças que o gateway teve e nunca chegaram por webhook.
   * Irmão do `resolve-cycle-count`: o mesmo caminho que a ingestão tenta
   * sozinha, aqui disparado à mão, com a falha do gateway voltando como erro
   * HTTP para o operador ver.
   */
  async syncBillingAgreementChargeHistory(
    billingAgreementId: string,
    body: SyncChargeHistoryInput,
  ): Promise<SyncChargeHistoryResult> {
    const { data } = await apiClient.post<SyncChargeHistoryResult>(
      `/admin/sales/billing-agreements/${billingAgreementId}/sync-charge-history`,
      body,
    );
    return data;
  }

  /** O mesmo, para as BAs do contrato que têm dessincronia. */
  async syncContractChargeHistory(
    contractId: string,
    body: SyncChargeHistoryInput,
  ): Promise<SyncContractChargeHistoryResult> {
    const { data } = await apiClient.post<SyncContractChargeHistoryResult>(
      `/admin/sales/contracts/${contractId}/sync-charge-history`,
      body,
    );
    return data;
  }

  async createDebtArrangement(
    body: CreateDebtArrangementInput,
  ): Promise<DebtArrangement> {
    const { data } = await apiClient.post<DebtArrangement>(
      '/admin/sales/debt-arrangements',
      body,
    );
    return data;
  }

  async activateDebtArrangement(
    debtArrangementId: string,
    body: ActivateDebtArrangementInput,
  ): Promise<DebtArrangement> {
    const { data } = await apiClient.post<DebtArrangement>(
      `/admin/sales/debt-arrangements/${debtArrangementId}/activate`,
      body,
    );
    return data;
  }

  async updateDebtArrangementDraft(
    debtArrangementId: string,
    body: UpdateDebtArrangementDraftInput,
  ): Promise<DebtArrangement> {
    const { data } = await apiClient.patch<DebtArrangement>(
      `/admin/sales/debt-arrangements/${debtArrangementId}/draft`,
      body,
    );
    return data;
  }

  async cancelDebtArrangement(
    debtArrangementId: string,
    body: DebtArrangementReasonInput,
  ): Promise<DebtArrangement> {
    const { data } = await apiClient.post<DebtArrangement>(
      `/admin/sales/debt-arrangements/${debtArrangementId}/cancel`,
      body,
    );
    return data;
  }

  async revertDebtArrangement(
    debtArrangementId: string,
    body: DebtArrangementReasonInput,
  ): Promise<unknown> {
    const { data } = await apiClient.post<unknown>(
      `/admin/sales/debt-arrangements/${debtArrangementId}/revert`,
      body,
    );
    return data;
  }

  async abandonContract(
    contractId: string,
    body: AbandonContractInput,
  ): Promise<AbandonContractResult> {
    const { data } = await apiClient.post<AbandonContractResult>(
      `/admin/sales/contracts/${contractId}/abandon`,
      body,
    );
    return data;
  }

  // --- Contas do gateway ---

  async listProviderAccounts(
    input?: ListProviderAccountsInput,
  ): Promise<ProviderAccount[]> {
    const { data } = await apiClient.get<ProviderAccount[]>(
      '/admin/sales/provider-accounts',
      { params: { provider: input?.provider || undefined } },
    );
    return data;
  }

  // --- Webhook Inbox (log de webhooks recebidos) ---

  async listWebhookInbox(
    input?: ListSaleWebhookInboxInput,
  ): Promise<SaleWebhookInboxListResponse> {
    const { data } = await apiClient.get<SaleWebhookInboxListResponse>(
      '/admin/sales/webhook-inbox',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          status: input?.status || undefined,
          eventType: input?.eventType || undefined,
          providerAccountId: input?.providerAccountId || undefined,
          idempotenceSource: input?.idempotenceSource || undefined,
          from: input?.from || undefined,
          to: input?.to || undefined,
        },
      },
    );
    return data;
  }

  /** Uma row, para o modal "Webhook de origem" da aba Auditoria. */
  async getWebhookInboxRow(id: string): Promise<SaleWebhookInbox> {
    const { data } = await apiClient.get<SaleWebhookInbox>(
      `/admin/sales/webhook-inbox/${id}`,
    );
    return data;
  }

  async forceProcessWebhookInbox(
    id: string,
    body: ForceProcessSaleWebhookInboxInput,
  ): Promise<SaleWebhookInbox> {
    const { data } = await apiClient.post<SaleWebhookInbox>(
      `/admin/sales/webhook-inbox/${id}/force-process`,
      body,
    );
    return data;
  }

  // --- Baixa manual e anulação de lançamento ---

  /**
   * Sobe o anexo da justificativa e devolve o `fileId` que a operação leva no
   * corpo. O corpo **é** o arquivo cru (sem multipart): nome no `x-file-name`,
   * tipo no `Content-Type` — mesmo contrato do anexo de card do pipefy.
   */
  async uploadAttachment(file: File): Promise<UploadAttachmentResult> {
    const { data } = await apiClient.post<UploadAttachmentResult>(
      '/admin/sales/attachments',
      file,
      {
        headers: {
          'x-file-name': encodeURIComponent(file.name),
          'Content-Type': file.type || 'application/octet-stream',
        },
      },
    );
    return data;
  }

  async manuallySettleInvoice(
    invoiceId: string,
    body: ManuallySettleInvoiceInput,
  ): Promise<ManuallySettleInvoiceResult> {
    const { data } = await apiClient.post<ManuallySettleInvoiceResult>(
      `/admin/sales/invoices/${invoiceId}/manual-settlement`,
      body,
    );
    return data;
  }

  // --- Alertas operacionais (a fila humana do módulo, ver `SalesAlertType`) ---

  async listOperationalAlertFacets(
    status?: SalesOperationalAlertStatus,
  ): Promise<OperationalAlertFacet[]> {
    const { data } = await apiClient.get<OperationalAlertFacet[]>(
      '/admin/sales/operational-alerts/facets',
      { params: { status } },
    );
    return data;
  }

  async listOperationalAlerts(
    input?: ListSalesOperationalAlertsInput,
  ): Promise<SalesOperationalAlertsListResponse> {
    const { data } = await apiClient.get<SalesOperationalAlertsListResponse>(
      '/admin/sales/operational-alerts',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          status: input?.status || undefined,
          type: input?.type || undefined,
          entityType: input?.entityType || undefined,
          entityId: input?.entityId || undefined,
          order: input?.order,
          closedForEntity: input?.closedForEntity,
        },
      },
    );
    return data;
  }

  async resolveOperationalAlert(
    alertId: string,
    body: CloseOperationalAlertInput,
  ): Promise<SalesOperationalAlert> {
    const { data } = await apiClient.post<SalesOperationalAlert>(
      `/admin/sales/operational-alerts/${alertId}/resolve`,
      body,
    );
    return data;
  }

  async dismissOperationalAlert(
    alertId: string,
    body: CloseOperationalAlertInput,
  ): Promise<SalesOperationalAlert> {
    const { data } = await apiClient.post<SalesOperationalAlert>(
      `/admin/sales/operational-alerts/${alertId}/dismiss`,
      body,
    );
    return data;
  }

  async reopenOperationalAlertForEntity(
    alertId: string,
    body: { note: string },
  ): Promise<SalesOperationalAlert> {
    const { data } = await apiClient.post<SalesOperationalAlert>(
      `/admin/sales/operational-alerts/${alertId}/reopen-for-entity`,
      body,
    );
    return data;
  }

  async invalidateManualTransaction(
    transactionId: string,
    body: JustifiedActionInput,
  ): Promise<InvalidateManualTransactionResult> {
    const { data } = await apiClient.post<InvalidateManualTransactionResult>(
      `/admin/sales/transactions/${transactionId}/invalidate`,
      body,
    );
    return data;
  }
}
